import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-03-29'
        tag='Poshmark'
        title='Spring is Finally Here: What You Should Sell This Season On Poshmark'
      >
        <p>
          With over two million app downloads this year, Poshmark has cemented itself as one of the top online
          marketplaces. From designers, vintage, pre-loved to consignment items, users can find and sell all types of
          clothing, shoes, and accessories.
        </p>
        <p>
          Now that Spring is here, people would definitely be doing "spring cleaning" and pulling out pieces from their
          closets that no longer fit them or they no longer like. This is a great time for sellers to review their
          inventory and bring in trendy pieces to attract this batch of potential buyers. But how do you exactly do it?
          Here's a short guide on what to sell on Poshmark this season.
        </p>

        <h3>Why sell in-season?</h3>
        <p>
          While pre-loved and vintage items sell on Poshmark, there are times when certain ones are unable to be
          appreciated by buyers. Not all items can be considered "staple" or "classic" pieces that you can wear even
          after years since their launch from a brand or a designer.
        </p>
        <p>
          However, there are vintage ones that are trendy enough and end up circulating on social media. Gen Z's and
          even Millennials are keen on purchasing these rare items for their collection. Even brands such as Forever21,
          H&M, and Urban Outfitters are collaborating with vintage fashion labels to serve the needs of this particular
          market. But of course, sellers have to be selective in getting these clothes for their inventory.
        </p>
        <p>
          But now that it is finally Spring, you may want to keep these vintage pieces to a minimum and opt to sell the
          trendier ones. Young shoppers are all about dressing up in modern clothes and accessories, especially the bold
          and bright colored ones. The market is still experimenting with their styles, so having stylish items will
          definitely be advantageous to you.
        </p>
        <p>
          You can source these items from consignment, retail, and even department stores. Check out the Spring 2022
          collection of a particular brand or label that sells well, even in the online marketplace. Naturally, these
          are expensive as they are still in season. But it would certainly be worth it, especially since you are
          targeting to close more sales–and selling chic and popular clothes is a sure way to achieve it.
        </p>

        <h3>What's trending?</h3>
        <p>
          For Spring 2022,{' '}
          <a href='https://blog.poshmark.com/2022/03/21/spring-into-poshmark-everything-you-need-to-know-to-turn-your-closet-into-cash/'>
            Poshmark
          </a>{' '}
          says popular categories include “swimsuit,” vacation wear,” and even “festival wear.” Since summer is also
          around the corner, people are starting to buy swimsuits, sunglasses, beachwear, and other accessories suited
          for beach trips or weekend getaways. This season is about going on vacations, so colorful dresses, floral
          shirts, bright-hued shorts, and other bottoms are now back in style.
        </p>
        <p>
          As for the palette, bold and cheerful colors are trending now. You will be seeing more of this, from neon
          greens, pinks, bold blues to lemon yellows for the next few weeks. Take note of warm hues such as marigold,
          mauve, peach, coral, mustard, olive green, and teal that will take up the season's palette.
        </p>
        <p>
          A tip for choosing what to sell: have at least four or more color options for each clothing piece you sell.
          This allows you to sell more of one kind and buyers also have the freedom to purchase two or more colors of
          the same item, especially if they are having difficulty choosing which hue suits them the best. In this way,
          you will easily close sales in such a short time and will have the space to replace your inventory.
        </p>
        <p>
          In terms of the overall style, new bohemian (think of maxi dresses and skirts, crochet tops and bags, and
          espadrilles) and modern preppy (think of sweaters, platform shoes, and pearls) are returning this season. So,
          you should stock up on these pieces and try mixing them with other staples so you have more options to offer
          to your potential customers.
        </p>

        <h3>Upsell your listings</h3>
        <p>
          A good way to make your listings more attractive is to be complete and engaging with your photos and
          descriptions.
        </p>
        <p>
          For photos, make sure that you leave a good impression on your potential buyers. Put extra effort as images
          determine whether a Poshmark user will purchase or inquire about your item.
        </p>
        <p>
          To give you a reference, check out Pinterest and Instagram for photos of items similar to yours. These apps
          are known for their attractive images that are sometimes taken by professionals. But of course, you don't need
          a professional camera or equipment–your phone will do.{' '}
        </p>
        <p>
          Take photos using natural light, so the details of the items are seen. You can also shoot outdoors to have a
          better outcome on your photos. Otherwise, opt for hanging up the clothes or laying out the shoes and
          accessories against a flat, plain background. In doing so, your items' colors and details can be clearly seen.
        </p>
        <p>
          After getting good images, make sure that you properly label your items. Always include the brand name in the
          title and description. A lot of shoppers are brand conscious. Most believe that these are high-quality items
          as they have gone through comprehensive manufacturing and product checking processes.
        </p>
        <p>
          In your photos, make sure the logo and/or brand name are visible so that potential buyers will recognize and
          can show the authenticity of your items.
        </p>
        <p>
          Additionally, it is best to indicate when your item was bought, especially if it is part of a brand's Spring
          2022 collection. Again, people at this time prefer to get their hands on modern, trendy pieces. So, having
          such a description at the topmost part will help you attract customers.
        </p>
        <p>
          Lastly, make sure you are transparent about the condition of everything you are selling. Nothing turns off a
          potential client more than a deceptive description and photo. This applies especially to secondhand pieces.
        </p>
        <p>
          For instance, if you are selling a floral top that is trendy or suitable for Spring but was bought two years
          ago, be honest about its condition or any flaws. Be it a small part that faded or a minor tear on the
          stitching; customers would want to know all about these to help them gauge whether they still want to purchase
          or not. This adds to your credibility as a seller.
        </p>

        <h3>A pro-tip: cross-promote listings</h3>
        <p>
          While having chic and in-season items can attract customers, you would want extensive exposure beyond
          Poshmark. Cross-promoting your listings on other marketplaces like Mercari and eBay (coming soon) is possible
          with an app like OneShop.
        </p>
        <p>
          When you subscribe to OneShop, you can cross-list your items to other online social commerce platforms. You
          don't need to do this manually, as the app automatically does it for you.
        </p>
        <p>
          Once you sell an item from one marketplace, OneShop delists this from the other marketplaces where the item is
          also listed to help you avoid selling the same item to different customers by accident.{' '}
        </p>
        <p>
          Within a few clicks, you can gain exposure to other possible buyers and ultimately help you achieve your goal
          of closing more sales.
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
